// "We'll hold it for you" — the parked-flow destination (Figma 21 success
// variant). Entry point for the win-back email drips (flows A-D).
//
// Three routes lead here and they need different sentences, which is what
// `?from=` carries:
//   (absent)  no longer from step 2: since 2026-10-08 its "Add materials later"
//             saves, emails the link and goes to the dashboard (docs/31). Kept
//             for an old link or bookmark — nothing is framed yet
//   review    "Save for now" on the review screen — the submission is complete
//   trial     "Email me a reminder" after declining the trial — it is complete
//             AND blocked on membership, so the way back is step 4, which
//             offers the plan again
//
// The membership site's paywall (tests A and B) sends its Lite and close here with
// flow=submission. Since 2026-10-28 this page doesn't show for them: it forwards to step 4
// with checkout=declined, the same back-without-paying screen as tests C and D after
// Stripe (it holds the submission there). Drawing this page first made it blink between
// "Your submission has been saved" and "We'll hold it for you" while the hold came back.
//
// The hold date is the breakdown's own deadline: we cannot keep a submission
// past the point where sending it would achieve anything, so the copy promises
// exactly that day and no longer.

import React, { useEffect } from "react";
import { connect } from "react-redux";
import styled from "styled-components";
import i18next from "i18next";
import qs from "query-string";
import { iRootState } from "../../../store";
import { trackSubmissionEvent } from "../analytics";
import SubmissionLayout from "../common/SubmissionLayout";
import { monthDay } from "../common/dates";
import { withParam } from "../common/submissionParams";
import { goToDashboard } from "../common/dashboard";
import { membershipPrimary } from "../common/membershipButton";

interface Props extends StateProps {
  history: any;
  location: any;
}

// The membership site's own parameters, dropped on the way back into the flow (and k, a
// session key the paywall should already have dropped: never left on a flow URL).
const PAYWALL_ONLY_PARAMS = ["flow", "liteOffer", "sid", "k"];

const holdDate = monthDay;

const SubmissionSavedPage: React.FC<Props> = ({
  history,
  location,
  hold,
  draft,
}) => {
  const from = (qs.parse(location.search).from as string) || "";
  const fromMembershipPaywall =
    from === "trial" && new URLSearchParams(location.search).get("flow") === "submission";
  const until = holdDate(
    (hold && hold.holdUntilUtc) || (draft && draft.holdUntilUtc) || null
  );

  // The flow's query string, minus this page's own and the paywall's parameters.
  const flowSearch = ["from"]
    .concat(PAYWALL_ONLY_PARAMS)
    .reduce((search, name) => withParam(search, name, null), location.search as string);

  useEffect(() => {
    if (fromMembershipPaywall) {
      trackSubmissionEvent("sf_paywall_declined_forwarded", {});
      history.replace(`/submission/flow/step/4${withParam(flowSearch, "checkout", "declined")}`);
      return;
    }
    trackSubmissionEvent("sf_submission_saved", { from, fromMembershipPaywall });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const message = () => {
    if (until) {
      // The promise the win-back emails then have to keep.
      return i18next.t(
        "Nothing's lost. Come back before {{date}} and it's still here.",
        { date: until }
      );
    }
    return i18next.t(
      "We will get in touch with you via email to let you know about changes on your submission."
    );
  };

  // Where "finish it now" goes. From the review screen or the trial the
  // materials are already done, so sending them back to step 2 would be a
  // detour through work they have finished. The flow's query string goes
  // along (minus ?from=), so the breakdown and the step-4 pick survive.
  const resumePath =
    from === "trial"
      ? `/submission/flow/step/4${flowSearch}`
      : from === "review"
      ? `/submission/flow/step/4${flowSearch}`
      : `/submission/flow/step/2${flowSearch}`;

  // Forwarding: nothing of this page is drawn, so nothing blinks.
  if (fromMembershipPaywall) {
    return (
      <SubmissionLayout>
        <Centered aria-busy="true" />
      </SubmissionLayout>
    );
  }

  return (
    <SubmissionLayout>
      <Centered>
      <Icon aria-hidden="true">📩</Icon>
      <Title>
        {until
          ? i18next.t("We'll hold it for you")
          : i18next.t("Your submission has been saved")}
      </Title>
      <Message>{message()}</Message>
      <PrimaryButton
        onClick={() => {
          trackSubmissionEvent("sf_saved_to_dashboard", { from });
          goToDashboard(history);
        }}
      >
        {i18next.t("Go to my dashboard")}
      </PrimaryButton>
      <TextButton onClick={() => history.push(resumePath)}>
        {i18next.t("Actually, finish my submission right now")}
      </TextButton>
      </Centered>
    </SubmissionLayout>
  );
};

const Centered = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  min-height: calc(100vh - 120px);
  padding: 24px 0;

  @media all and (min-width: 1024px) {
    min-height: 0;
    justify-content: flex-start;
    padding: 48px 0 0;
  }
`;

const Icon = styled.div`
  font-size: 44px;
  margin-bottom: 16px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #122640;
  margin: 0 0 12px;
`;

const Message = styled.p`
  font-size: 15px;
  color: #53627c;
  line-height: 22px;
  margin: 0 0 28px;
`;

const PrimaryButton = styled.button`
  width: 100%;
  max-width: 360px;
  ${membershipPrimary}
  padding: 14px 0;
  min-height: 48px;
`;

const TextButton = styled.button`
  background: none;
  border: none;
  color: #0f9bf0;
  font-size: 14px;
  font-weight: 600;
  margin-top: 16px;
  cursor: pointer;
  min-height: 44px;
`;


interface StateProps {
  hold: iRootState["submissionStep4Model"]["hold"];
  draft: iRootState["submissionSignupModel"]["draft"];
}

const mapStateToProps = (state: iRootState): StateProps => ({
  hold: state.submissionStep4Model.hold,
  draft: state.submissionSignupModel.draft,
});

export default connect(mapStateToProps)(SubmissionSavedPage);
